import { Badge } from 'components/ui/badge';
import { ComponentProps, ReactNode } from 'react';
import { cn } from 'utils/cn';

export type ChipType =
    | 'neutral'
    | 'success'
    | 'warning'
    | 'error'
    | 'critical'
    | 'outlined'
    | 'inactive'
    | 'link'
    | 'custom'
    | 'info'
    | 'reviewed'
    | 'finalized'
    | 'credited'
    | 'compliance-success'
    | 'compliance-warning'
    | 'compliance-error'
    | 'draft'
    | undefined;

/**
 * What each status looks like. `custom` is the white base, for a caller that colours the chip itself. The new
 * design draws its own colours for the tones it has, and for `inactive` and `finalized`, which classic's colours
 * leave too faint to read there; the other statuses keep classic's colours.
 */
const TYPE_CLASS: Record<Exclude<ChipType, undefined>, string> = {
    custom: 'bg-grey-0 text-fg',
    neutral: 'bg-grey-200 text-grey-a400 new:bg-neutral-tint new:text-foreground-secondary',
    success: 'bg-success-lighter text-fg-success new:bg-success-tint new:text-success-ink',
    warning: 'bg-warning-lighter text-fg-warning new:bg-warning-tint new:text-warning-ink',
    error: 'bg-error-lighter text-fg-error new:bg-danger-tint new:text-danger-ink',
    critical: 'bg-fg-error text-error-lighter',
    outlined: 'bg-grey-0 border-divider text-fg',
    inactive: 'bg-grey-a100 text-grey-400 new:bg-surface-subtle new:text-muted-foreground',
    link: 'bg-info-lighter text-link decoration-link cursor-pointer select-auto hover:underline',
    info: 'bg-info-lighter text-link',
    reviewed: 'bg-reviewed-lighter text-reviewed',
    finalized: 'bg-grey-0 border-divider text-fg-success new:text-success-ink',
    credited: 'bg-credited-lighter text-link',
    // The contact merge's compliance statuses, a shade darker than `success`/`warning`/`error`; drawn `compact`.
    'compliance-success': 'bg-success-light text-success-dark',
    'compliance-warning': 'bg-warning-light text-warning-darker',
    'compliance-error': 'bg-error-light text-error-darker',
    // A record saved for later. The dashed edge tells it from `outlined`, and from a status that is also called Draft.
    draft: 'bg-grey-0 text-fg border-dashed border-grey-500 new:bg-changed new:text-changed-ink new:border-changed-border',
};

/**
 * The new design's small and medium chips are 20px pills in 12px type: the small one bolder, the medium one lighter.
 * The medium one's 14px line is a ratio of those 12px, so a caller that sets another size sets `new:leading-*`
 * with it: 14px over its own size.
 */
const SIZE_CLASS = {
    small: 'min-h-4 min-w-4 px-1.5 text-[10px] leading-4 new:text-caption new:min-h-5 new:rounded-lg new:leading-[1.6667]',
    medium: 'min-h-6 min-w-6 px-2 text-xs leading-5 new:min-h-5 new:rounded-lg new:py-[3px] new:leading-[1.17] new:font-medium',
    compact: 'h-5.5 rounded-[4px] px-2 py-0 text-xs leading-5.5 font-medium',
} as const;

interface StatusChipProps extends Omit<ComponentProps<'span'>, 'children'> {
    label?: ReactNode;
    type?: ChipType;
    size?: keyof typeof SIZE_CLASS;
    /** Rendered before the label; on its own when there is no label. With neither, there is no chip. */
    icon?: ReactNode;
    /** Makes the chip a link that opens in a new tab. */
    link?: string;
}

export default function StatusChip({
    label,
    type = 'neutral',
    size = 'medium',
    icon,
    link,
    className,
    ...rest
}: StatusChipProps) {
    // A record with no value yet, such as a draft with no status, draws no chip rather than an empty one.
    if ((label ?? '') === '' && icon === undefined) return null;

    const chip = (
        <Badge
            variant="outline"
            className={cn(
                'h-auto rounded-sm border-transparent py-px font-semibold',
                SIZE_CLASS[size],
                icon !== undefined && label === undefined && 'px-0',
                TYPE_CLASS[type ?? 'neutral'],
                className
            )}
            {...rest}
        >
            {/* Wrapped, so the badge's own rule for a bare child SVG leaves the caller's icon size alone. */}
            {icon !== undefined && <span className="inline-flex">{icon}</span>}
            {label}
        </Badge>
    );

    if (link) {
        return (
            <a target="_blank" href={link} rel="noreferrer" className="no-underline outline-offset-0">
                {chip}
            </a>
        );
    }
    return chip;
}
